import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./UserInfoForm.module.css";
import { searchZips } from "../../../lib/zipApi";
import { lookupNpi } from "../../../lib/store";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Outline location pin (inherits color via currentColor) — replaces the
// colored 📍 emoji so it matches the form's styling.
function LocationIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

export const validateUserInfo = (values) => {
  const errors = {};
  if (!values.firstName || values.firstName.trim().length < 2)
    errors.firstName = "Please enter your first name.";
  if (!values.lastName || values.lastName.trim().length < 2)
    errors.lastName = "Please enter your last name.";
  if (!values.practiceName || values.practiceName.trim().length < 2)
    errors.practiceName = "Please enter your practice name.";
  if (!values.role) errors.role = "Please select your role.";
  if (!values.workEmail || !EMAIL_RE.test(values.workEmail.trim()))
    errors.workEmail = "Please enter a valid work email.";
  if (String(values.zipCode || "").replace(/\D/g, "").length !== 5)
    errors.zipCode = "Please enter a valid 5-digit ZIP code.";
  // npi is optional.
  return errors;
};

/* "See your snapshot" details form. Controlled by the parent; reports validity
   through validateUserInfo so the parent can gate the Continue button. */
const UserInfoForm = ({ values, onChange, submitAttempted, roleOptions = [], consent }) => {
  const [touched, setTouched] = useState({});
  const errors = useMemo(() => validateUserInfo(values), [values]);

  // ZIP → State + City via GeoNames. As the user types, matching ZIPs are
  // shown in a dropdown; an exact 5-digit match is also resolved into the form.
  const [zipStatus, setZipStatus] = useState({ loading: false, state: "", city: "", error: "" });
  const [zipSuggestions, setZipSuggestions] = useState([]);
  const [zipOpen, setZipOpen] = useState(false);
  const skipZipLookup = useRef(false); // skip the lookup right after a pick

  useEffect(() => {
    const zip = String(values.zipCode || "").replace(/\D/g, "");

    if (skipZipLookup.current) {
      skipZipLookup.current = false;
      return;
    }
    if (zip.length < 2) {
      setZipSuggestions([]);
      setZipOpen(false);
      setZipStatus({ loading: false, state: "", city: "", error: "" });
      return;
    }

    let cancelled = false;
    setZipStatus((s) => ({ ...s, loading: true, error: "" }));
    const t = setTimeout(() => {
      searchZips(zip, 6)
        .then((rows) => {
          if (cancelled) return;
          setZipSuggestions(rows);
          setZipOpen(rows.length > 0);
          const exact = rows.find((r) => r.zip === zip);
          if (exact) {
            setZipStatus({ loading: false, state: exact.state, city: exact.city, error: "" });
            onChange((prev) => ({ ...prev, state: exact.state, city: exact.city }));
          } else {
            setZipStatus({
              loading: false,
              state: "",
              city: "",
              error: rows.length ? "" : "No match for this ZIP.",
            });
          }
        })
        .catch(() => {
          if (cancelled) return;
          setZipSuggestions([]);
          setZipOpen(false);
          setZipStatus({ loading: false, state: "", city: "", error: "ZIP lookup failed." });
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.zipCode]);

  // User clicked a suggestion → fill ZIP + resolved state/city, close dropdown.
  const pickZip = (s) => {
    skipZipLookup.current = true;
    setZipSuggestions([]);
    setZipOpen(false);
    setZipStatus({ loading: false, state: s.state, city: s.city, error: "" });
    onChange((prev) => ({ ...prev, zipCode: s.zip, state: s.state, city: s.city }));
  };

  // NPI (optional) — verify against the CMS registry once 10 digits are typed.
  // Purely informational; it never blocks the form.
  const [npiStatus, setNpiStatus] = useState({ loading: false, valid: null, info: "" });
  useEffect(() => {
    const npi = String(values.npi || "").replace(/\D/g, "");
    if (npi.length !== 10) {
      setNpiStatus({ loading: false, valid: null, info: "" });
      return;
    }
    let cancelled = false;
    setNpiStatus({ loading: true, valid: null, info: "" });
    lookupNpi(npi)
      .then((data) => {
        if (cancelled) return;
        const first = data?.results?.[0];
        if (data?.result_count > 0 && first) {
          const basic = first.basic || {};
          if (basic.status && basic.status !== "A") {
            setNpiStatus({ loading: false, valid: false, info: "NPI is not active." });
          } else {
            const name =
              basic.organization_name ||
              `${basic.first_name || ""} ${basic.last_name || ""}`.trim();
            const cred = basic.credential ? `, ${basic.credential}` : "";
            setNpiStatus({ loading: false, valid: true, info: `Verified: ${name}${cred}` });
          }
        } else {
          setNpiStatus({ loading: false, valid: false, info: "NPI not found in registry." });
        }
      })
      .catch(() => {
        if (!cancelled) setNpiStatus({ loading: false, valid: false, info: "Could not verify NPI." });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.npi]);

  const showError = (name) => (touched[name] || submitAttempted) && errors[name];

  const set = (name) => (e) => onChange({ ...values, [name]: e.target.value });
  const blur = (name) => () => setTouched((t) => ({ ...t, [name]: true }));

  const inputProps = (name) => ({
    id: `pa-${name}`,
    value: values[name] || "",
    onChange: set(name),
    onBlur: blur(name),
    "aria-invalid": !!showError(name),
    "aria-describedby": showError(name) ? `pa-${name}-err` : undefined,
  });

  const Error = ({ name }) =>
    showError(name) ? (
      <span id={`pa-${name}-err`} className={styles.errorText}>
        {errors[name]}
      </span>
    ) : null;

  return (
    <form className={styles.form} noValidate onSubmit={(e) => e.preventDefault()}>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="pa-firstName">
          First name<span className={styles.req} aria-hidden="true">*</span>
        </label>
        <input
          type="text"
          placeholder="Jane"
          className={`${styles.input} ${showError("firstName") ? styles.inputError : ""}`}
          {...inputProps("firstName")}
        />
        <Error name="firstName" />
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="pa-lastName">
          Last name<span className={styles.req} aria-hidden="true">*</span>
        </label>
        <input
          type="text"
          placeholder="Doe"
          className={`${styles.input} ${showError("lastName") ? styles.inputError : ""}`}
          {...inputProps("lastName")}
        />
        <Error name="lastName" />
      </div>

      <div className={`${styles.field} ${styles.fieldFull}`}>
        <label className={styles.label} htmlFor="pa-practiceName">
          Practice name<span className={styles.req} aria-hidden="true">*</span>
        </label>
        <input
          type="text"
          placeholder="Bright Eyes Optometry"
          className={`${styles.input} ${showError("practiceName") ? styles.inputError : ""}`}
          {...inputProps("practiceName")}
        />
        <Error name="practiceName" />
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="pa-role">
          Your role<span className={styles.req} aria-hidden="true">*</span>
        </label>
        <select
          className={`${styles.input} ${styles.select} ${showError("role") ? styles.inputError : ""}`}
          {...inputProps("role")}
        >
          <option value="">Select…</option>
          {roleOptions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <Error name="role" />
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="pa-zipCode">
          Zip code <span className={styles.req} aria-hidden="true">*</span>
        </label>
        <div className={styles.zipWrap}>
          <input
            type="text"
            inputMode="numeric"
            placeholder="90210"
            className={`${styles.input} ${zipStatus.loading ? styles.inputLoading : ""} ${
              showError("zipCode") ? styles.inputError : ""
            }`}
            autoComplete="off"
            {...inputProps("zipCode")}
            onFocus={() => zipSuggestions.length > 0 && setZipOpen(true)}
            onBlur={() => {
              blur("zipCode")();
              setTimeout(() => setZipOpen(false), 120);
            }}
          />
          {zipStatus.loading && (
            <span className={styles.inputSpinner} aria-label="Looking up ZIP" />
          )}
          {zipOpen && zipSuggestions.length > 0 && (
            <ul className={styles.zipMenu}>
              {zipSuggestions.map((s) => (
                <li key={s.zip}>
                  <button
                    type="button"
                    className={styles.zipOption}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickZip(s);
                    }}
                  >
                    <span className={styles.zipOptionIcon}>
                      <LocationIcon />
                    </span>
                    <strong>{s.zip}</strong>
                    <span className={styles.zipOptionPlace}>
                      {s.city}, {s.state}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {!zipStatus.loading && zipStatus.state && (
          <span className={`${styles.hintText} ${styles.hintOk}`}>
            <LocationIcon /> State: {zipStatus.state}
            {zipStatus.city ? ` · City: ${zipStatus.city}` : ""}
          </span>
        )}
        {!zipStatus.loading && zipStatus.error && (
          <span className={styles.errorText}>{zipStatus.error}</span>
        )}
        {!zipStatus.state && !zipStatus.error && <Error name="zipCode" />}
      </div>

      <div className={`${styles.field} ${styles.fieldFull}`}>
        <label className={styles.label} htmlFor="pa-workEmail">
          Work email<span className={styles.req} aria-hidden="true">*</span>
        </label>
        <input
          type="email"
          placeholder="jane@clinic.com"
          className={`${styles.input} ${showError("workEmail") ? styles.inputError : ""}`}
          {...inputProps("workEmail")}
        />
        <Error name="workEmail" />
      </div>

      <div className={`${styles.field} ${styles.fieldFull}`}>
        <label className={styles.label} htmlFor="pa-npi">
          NPI <span className={styles.optional}>(optional)</span>
        </label>
        <div className={styles.inputWrap}>
          <input
            type="text"
            inputMode="numeric"
            placeholder="10-digit NPI"
            className={`${styles.input} ${npiStatus.loading ? styles.inputLoading : ""}`}
            {...inputProps("npi")}
          />
          {npiStatus.loading && (
            <span className={styles.inputSpinner} aria-label="Verifying NPI" />
          )}
        </div>
        {!npiStatus.loading && npiStatus.info && (
          <span className={`${styles.hintText} ${npiStatus.valid ? styles.hintOk : styles.errorText}`}>
            {npiStatus.info}
          </span>
        )}
      </div>

      {consent && <p className={styles.consent}>{consent}</p>}
    </form>
  );
};

export default UserInfoForm;
